import { Media } from '@/types/model';
import { Camera, Image as ImageIcon, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useState } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { Button } from '../ui/button';

interface GalleryPreviewProps {
  siteName: string;
  images?: Media[];
  className?: string;
}

export function GalleryPreview({ siteName, images = [], className = '' }: GalleryPreviewProps) {
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (images.length === 0) return null;

  const heroImage = images[0];
  // Up to 4 thumbnails for the right grid
  const thumbImages = images.slice(1, 5);
  // Hidden PhotoView items for images beyond the visible grid (so lightbox can scroll them)
  const hiddenImages = images.slice(5);
  const extraCount = images.length - 5;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxVisible(true);
  };

  return (
    <PhotoProvider
      loop={true}
      maskClosable={true}
      pullClosable={true}
      maskOpacity={0.85}
      visible={lightboxVisible}
      index={lightboxIndex}
      onIndexChange={setLightboxIndex}
      onVisibleChange={(v) => setLightboxVisible(v)}
      overlayRender={() => null}
      toolbarRender={({ onScale, scale, onRotate, rotate, images: lightboxImages, index, onClose }) => (
        <div className="absolute right-0 bottom-0 left-0 z-[99999] flex items-center justify-between border-t bg-black/50 px-2 py-2 backdrop-blur-sm sm:px-4">
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="rounded bg-black/50 px-3 py-1.5 text-sm text-white backdrop-blur-sm">
              {index + 1} / {lightboxImages.length}
            </span>
            <span className="hidden min-w-[48px] text-xs text-white sm:inline">{Math.round(scale * 100)}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onScale(scale / 1.2);
              }}
              disabled={scale <= 0.5}
              className="size-8 border-white/20 bg-black/50 p-0 text-white hover:bg-white/20 hover:text-white"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onScale(scale * 1.2);
              }}
              disabled={scale >= 5}
              className="size-8 border-white/20 bg-black/50 p-0 text-white hover:bg-white/20 hover:text-white"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRotate((rotate + 90) % 360);
              }}
              className="size-8 border-white/20 bg-black/50 p-0 text-white hover:bg-white/20 hover:text-white"
              aria-label="Rotate clockwise"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onScale(1);
                onRotate(0);
              }}
              className="h-8 border-white/20 bg-white/10 px-2 text-white hover:bg-white/20 hover:text-white sm:px-3"
            >
              Đặt lại
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="size-8 border-white/20 bg-black/50 p-0 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}
    >
      {/* Register all images with PhotoView (hidden — lightbox is controlled) */}
      <div className="hidden">
        {images.map((img, i) => (
          <PhotoView key={img.id} src={img.image_url}>
            <img src={img.image_url} alt={img.alt || siteName} />
          </PhotoView>
        ))}
      </div>

      {/* ── Mobile: single hero + "Xem N ảnh" button ── */}
      <div className={`md:hidden ${className}`}>
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
          {heroImage.image_url ? (
            <img
              src={heroImage.image_url}
              alt={heroImage.alt || siteName}
              className="h-full w-full cursor-pointer object-cover"
              onClick={() => openLightbox(0)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100">
              <ImageIcon className="h-12 w-12 text-gray-400" />
            </div>
          )}
        </div>
        {images.length > 1 && (
          <button
            onClick={() => openLightbox(0)}
            className="mt-2 flex items-center gap-1.5 text-sm font-medium text-gray-700 underline-offset-2 hover:underline"
          >
            <Camera className="h-4 w-4" />
            Xem {images.length} ảnh
          </button>
        )}
      </div>

      {/* ── Desktop/Tablet: 1+4 Klook grid ── */}
      <div className={`hidden md:block ${className}`}>
        <div className="relative h-[400px] overflow-hidden rounded-2xl">
          {/* Tablet: [2fr_1fr], Desktop: [3fr_1fr_1fr] */}
          <div className="grid h-full grid-cols-[2fr_1fr] grid-rows-2 gap-[2px] lg:grid-cols-[3fr_1fr_1fr]">
            {/* Hero — spans 2 rows */}
            <div className="group relative row-span-2 cursor-pointer overflow-hidden" onClick={() => openLightbox(0)}>
              {heroImage.image_url ? (
                <img
                  src={heroImage.image_url}
                  alt={heroImage.alt || siteName}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-100">
                  <ImageIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Tablet: show first 2 thumbs (col 2, rows 1–2)
                            Desktop: 4 thumbs across 2 cols × 2 rows */}
            {thumbImages.map((img, i) => {
              // On tablet, only the first 2 thumbs are visible (col 2 only).
              // On desktop, all 4 thumbs appear (cols 2–3).
              // We hide thumb index 2 & 3 on tablet via CSS.
              const isTabletHidden = i >= 2;
              const isLast = i === 3;
              const showExtra = isLast && extraCount > 0;

              return (
                <div
                  key={img.id}
                  className={`group relative cursor-pointer overflow-hidden ${isTabletHidden ? 'hidden lg:block' : ''}`}
                  onClick={() => openLightbox(i + 1)}
                >
                  {img.image_url ? (
                    <img
                      src={img.image_url}
                      alt={img.alt || `${siteName} ${i + 2}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-100">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}

                  {/* +N overlay on last visible thumbnail */}
                  {showExtra && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-lg font-semibold text-white">+{extraCount}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* "Thư viện ảnh" overlay button */}
          <button
            onClick={() => openLightbox(0)}
            className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
          >
            <Camera className="h-3.5 w-3.5" />
            Thư viện ảnh ({images.length})
          </button>
        </div>
      </div>
    </PhotoProvider>
  );
}
